import { FormStore, DEFAULT_BREAKPOINTS, BREAKPOINT_ORDER, isPathPrefix, ERROR_STORE_SUFFIX, TOUCH_STORE_SUFFIX, runRenderNodeResolution, resolveAction, MODIFIER_KEY, LIST_SEMAPHORE, LIST_ITEM, PATH_MODIFIERS_KEY, resolveStorePath, computeListSliceRange, LIST_LENGTH, LIST_ITEM_PER_PAGE, LIST_PAGE, V_CHILDREN, V_COMP, expandSimplifiedNode, getOwnPathModifiers, mergeEffectivePathModifiers, normalizePath, V_VALIDATIONS, buildValidationRegistry } from '@jsonui/core';
export { ACTION_KEY, BREAKPOINT_ORDER, DEFAULT_BREAKPOINTS, ERROR_STORE_SUFFIX, FormStore, LIST_ITEM, LIST_ITEM_PER_PAGE, LIST_LENGTH, LIST_PAGE, LIST_SEMAPHORE, MODIFIER_KEY, PATH_MODIFIERS_KEY, TOUCH_STORE_SUFFIX, V_CHILDREN, V_COMP, actions, expandSimplifiedNode, modifiers } from '@jsonui/core';
import { jsx, jsxs, Fragment as Fragment$1 } from 'react/jsx-runtime';
import React, { useMemo, useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';

const isRecord = (value) => {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
};

/**
 * Single-root store hook.
 *
 * - initialStore: a single FormStore instance (optional)
 * - defaultValues: Record<storeName, JSON> where each value is a JSON object
 *   representing that logical store's root.
 */
const useFormStore = (initialStore, defaultValues) => {
    return useMemo(() => {
        const formStore = initialStore ?? new FormStore();
        if (isRecord(defaultValues)) {
            for (const [name, data] of Object.entries(defaultValues)) {
                if (typeof name !== 'string' || name.length === 0)
                    continue;
                // Initialise logical stores without marking them as "touched" –
                // touched tracking is for user interactions, not default values.
                formStore.setForStore(name, '/', data, false);
            }
        }
        return formStore;
    }, [initialStore, defaultValues]);
};

const View = ({ children, style, $ctx: _ctx, ...rest }) => {
    return (jsx("div", { style: style, ...rest, children: children }));
};

const Text = ({ children, style, $ctx: _ctx, ...rest }) => {
    return (jsx("p", { style: style, ...rest, children: children }));
};

const Button = ({ children, onPress, onClick, style, disabled, $ctx: _ctx, ...rest }) => {
    return (jsx("button", { style: {
            ...style,
            ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
        }, onClick: (onPress ?? onClick), ...rest, children: children }));
};

/** Render any incoming value as the string the input element displays. */
const toDisplayString = (value) => (value == null ? '' : String(value));
/**
 * Parse the raw string typed into a numeric input into a real JSON number.
 * Works for every numeric form an `<input type="number">` can hold (integers,
 * decimals, negatives, exponential, leading `+`). Returns `null` for empty or
 * incomplete input so the store never holds `NaN`.
 */
const parseNumber = (raw) => {
    if (raw.trim() === '')
        return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
};
/**
 * Hookless helper for store-bound text/number inputs.
 *
 * Instead of a controlled `value` (which jumps the caret and gets clobbered by
 * slow/async stores), it returns props for an **uncontrolled** input:
 *
 * - `defaultValue` seeds the initial text; afterwards the DOM owns it, so the
 *   browser keeps the caret exactly where the user put it and a slow store can
 *   never overwrite in-flight keystrokes.
 * - `ref` is a fresh callback every render, so React re-invokes it on each
 *   commit; it pushes the latest store value into the field **only while the
 *   field is not focused** (external resets, transform-on-get, other-field
 *   updates, and normalisation like `"42.50"` → `42.5` after editing ends).
 * - `onChange` coerces numeric inputs to a JSON `number` (or `null` when empty)
 *   before calling the supplied handler.
 *
 * Spread `defaultValue` + `onChange` onto the input and attach `ref` to the
 * real `<input>` element. For MUI's `TextField`, pass `ref` as `inputRef`.
 *
 * Note: no `useState`/`useEffect`/`useRef` — works in any component, hook-free.
 */
const uncontrolledInputProps = (value, onChange, type) => {
    const display = toDisplayString(value);
    const ref = (el) => {
        if (el == null)
            return;
        // Don't disturb the user while they're editing — this is what keeps the
        // caret stable and prevents a slow store from clobbering keystrokes.
        if (typeof document !== 'undefined' && document.activeElement === el)
            return;
        if (el.value !== display)
            el.value = display;
    };
    const handleChange = (e) => {
        if (type === 'number') {
            const numeric = parseNumber(e.target.value);
            // The store binding only reads `e.target.value`, so a synthetic event
            // carrying the numeric value persists a real JSON number.
            const syntheticEvent = {
                ...e,
                target: { ...e.target, value: numeric },
                currentTarget: e.currentTarget,
            };
            onChange?.(syntheticEvent);
            return;
        }
        onChange?.(e);
    };
    return { defaultValue: display, onChange: handleChange, ref };
};

const Edit = ({ value, onChange, onPress, style, $ctx, helperText, label, ...rest }) => {
    const { fieldErrors, fieldTouched } = $ctx ?? {};
    const handleChange = (onChange ?? onPress);
    const inputType = rest.type;
    const { defaultValue, onChange: inputOnChange, ref: inputRef } = uncontrolledInputProps(value, handleChange, inputType);
    return (jsxs("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
        }, children: [label ? (jsx("div", { style: {
                    fontSize: 14,
                    marginTop: 12,
                }, children: jsxs(Fragment$1, { children: [label, " ", fieldTouched ? `(touched)` : ''] }) })) : null, jsx("input", { ...rest, ref: inputRef, style: style, defaultValue: defaultValue, onChange: inputOnChange }), fieldErrors || helperText ? (jsx("div", { style: {
                    color: fieldErrors ? '#c00' : undefined,
                    fontSize: 12,
                    marginTop: 4,
                }, children: fieldErrors ? String(fieldErrors) : jsx(Fragment$1, { children: helperText }) })) : null] }));
};

const Slider = ({ value, onChange, onInput, min = 0, max = 100, step = 1, style, $ctx: _ctx, ...rest }) => {
    const changeHandler = (onChange ?? onInput);
    return (jsxs("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
        }, children: [jsx("input", { type: "range", min: min, max: max, step: step, value: (value ?? 0), onChange: changeHandler, style: style, ...rest }), jsx("div", { style: {
                    fontSize: 11,
                    marginTop: 4,
                    textAlign: 'right',
                    color: '#555',
                }, children: String(value ?? '') })] }));
};

const Fragment = ({ children }) => {
    return jsx(Fragment$1, { children: children });
};

const Image = ({ src, alt, style, $ctx: _ctx, ...rest }) => {
    return jsx("img", { src: src, alt: alt != null ? String(alt) : '', style: style, ...rest });
};

const StoreDebugger = ({ data, error, touched }) => {
    return (jsxs("div", { style: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 8,
            fontFamily: 'monospace',
            fontSize: 11,
            background: '#f5f5f5',
            padding: 8,
            borderRadius: 4,
            overflowX: 'auto',
        }, children: [jsxs("div", { children: [jsx("div", { style: { fontWeight: 'bold', marginBottom: 4 }, children: "data" }), jsx("pre", { style: { margin: 0, whiteSpace: 'unset' }, children: JSON.stringify(data, null, 2) })] }), jsxs("div", { children: [jsx("div", { style: { fontWeight: 'bold', marginBottom: 4 }, children: "error" }), jsx("pre", { style: { margin: 0, whiteSpace: 'unset' }, children: JSON.stringify(error, null, 2) })] }), jsxs("div", { children: [jsx("div", { style: { fontWeight: 'bold', marginBottom: 4 }, children: "touched" }), jsx("pre", { style: { margin: 0, whiteSpace: 'unset' }, children: JSON.stringify(touched, null, 2) })] })] }));
};

const SubmitButton = ({ children, style, $ctx, onClick, ...rest }) => {
    const { fieldErrors, fieldTouched } = $ctx ?? {};
    const disabled = !fieldTouched || !!fieldErrors;
    return (jsx("button", { style: {
            ...style,
            ...(disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
        }, disabled: disabled, 
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        onClick: onClick, ...rest, children: children }));
};

const FormLayout = ({ children, $childTop, $childBottom, style, $ctx: _ctx, ...rest }) => {
    return (jsxs("div", { style: {
            border: '1px solid #ddd',
            borderRadius: 6,
            padding: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            ...style,
        }, ...rest, children: [$childTop ? (jsx("div", { style: {
                    borderBottom: '1px solid #eee',
                    paddingBottom: 8,
                    marginBottom: 4,
                }, children: $childTop })) : null, jsxs("form", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                }, onSubmit: (e) => {
                    // SubmitButton handles its own click + submit logic; prevent the
                    // native form submission from reloading the page.
                    e.preventDefault();
                }, children: [jsx("div", { style: {
                            border: '1px dashed #ddd',
                            padding: 8,
                        }, children: children }), $childBottom ? (jsx("div", { style: {
                            borderTop: '1px solid #eee',
                            paddingTop: 8,
                            marginTop: 4,
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }, children: $childBottom })) : null] })] }));
};

/**
 * Fallback when `$comp` is unknown (main JsonUI `_Undefined` parity).
 */
const Undefined = (props) => {
    const name = props.compName ?? props.$comp ?? 'unknown';
    // TODO need better style
    return (jsxs("p", { style: {
            flex: 1,
            minWidth: 100,
            minHeight: 20,
            marginTop: 5,
            border: '2px solid #eab308',
            padding: 8,
            fontSize: 12,
        }, children: ["Unknown component: ", jsx("strong", { children: String(name) })] }));
};

const builtinComponents = {
    View,
    Text,
    Button,
    Edit,
    Fragment,
    Image,
    StoreDebugger,
    SubmitButton,
    FormLayout,
    Slider,
    _Undefined: Undefined,
};

const defaultGetWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 1024);
/**
 * Get current breakpoint key from viewport width (mobile-first).
 * Uses the largest breakpoint whose threshold is <= width.
 */
const getBreakpointFromWidth = (width, breakpoints) => {
    let current = 'base';
    for (const key of BREAKPOINT_ORDER) {
        const threshold = breakpoints[key];
        if (width >= threshold) {
            current = key;
        }
    }
    return current;
};
/**
 * Returns the current breakpoint based on viewport width.
 * On web, use default getWidth (window.innerWidth).
 * On React Native, pass getWidth: () => Dimensions.get('window').width.
 */
const useBreakpoint = (options = {}) => {
    const { getWidth = defaultGetWidth, breakpoints = DEFAULT_BREAKPOINTS } = options;
    const [breakpoint, setBreakpoint] = useState(() => getBreakpointFromWidth(getWidth(), breakpoints));
    useEffect(() => {
        const update = () => {
            setBreakpoint((prev) => {
                const next = getBreakpointFromWidth(getWidth(), breakpoints);
                return next !== prev ? next : prev;
            });
        };
        update();
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
    }, [getWidth, breakpoints]);
    return breakpoint;
};

const defaultConfig = {
    platform: 'web',
    breakpoint: 'md',
};
const StyleContext = createContext(defaultConfig);
/**
 * Provides platform and breakpoint to the style layer.
 * Wrap your app or JsonUI tree with this to enable responsive and platform-specific style resolution.
 */
const StyleProvider = ({ children, platform = 'web', breakpoint: breakpointProp, getWidth, breakpoints = DEFAULT_BREAKPOINTS, }) => {
    const breakpointFromHook = useBreakpoint(breakpointProp === undefined ? { getWidth, breakpoints } : {});
    const breakpoint = breakpointProp ?? breakpointFromHook;
    const value = useMemo(() => ({ platform, breakpoint }), [platform, breakpoint]);
    return jsx(StyleContext.Provider, { value: value, children: children });
};
const useStyleConfig = () => {
    return useContext(StyleContext);
};

const isShadowStore = (storeName) => storeName.endsWith(ERROR_STORE_SUFFIX) || storeName.endsWith(TOUCH_STORE_SUFFIX);
const useRenderNodeResolution = ({ node, modifiers, formStore, currentPath, effectivePathModifiers, validators, translations, defaultLanguage, activeLanguage, stylePlatform, styleBreakpoint, componentStore, componentPath, }) => {
    const [resolvedState, setResolvedState] = useState(null);
    const [resolveError, setResolveError] = useState(null);
    const dependenciesRef = useRef([]);
    const runResolutionRef = useRef(() => { });
    const resolutionVersionRef = useRef(0);
    useEffect(() => {
        const unsubscribe = typeof formStore.subscribeChange === 'function'
            ? formStore.subscribeChange((changedStore, changedPath) => {
                const deps = dependenciesRef.current;
                for (const dep of deps) {
                    if (dep.store !== changedStore)
                        continue;
                    if (isPathPrefix(dep.path, changedPath) || isPathPrefix(changedPath, dep.path)) {
                        runResolutionRef.current({ skipInlineValidation: isShadowStore(changedStore) });
                        break;
                    }
                }
            })
            : undefined;
        return () => {
            if (unsubscribe)
                unsubscribe();
        };
    }, [formStore]);
    /* eslint-disable react-hooks/exhaustive-deps -- legacy JsonUI resolution deps */
    useEffect(() => {
        let cancelled = false;
        setResolveError(null);
        const ctx = {
            formStore,
            currentPath,
            pathModifiers: effectivePathModifiers,
            validators,
            translations,
            defaultLanguage,
            activeLanguage,
        };
        const run = async (version, skipInlineValidation = false) => {
            try {
                const { state, deps } = await runRenderNodeResolution({
                    node,
                    modifiers,
                    ctx,
                    currentPath,
                    effectivePathModifiers,
                    stylePlatform,
                    styleBreakpoint,
                    componentStore,
                    componentPath,
                    skipInlineValidation,
                });
                if (!cancelled && resolutionVersionRef.current === version) {
                    setResolvedState(state);
                    dependenciesRef.current = deps;
                }
            }
            catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                if (!cancelled && resolutionVersionRef.current === version) {
                    setResolveError(error);
                    setResolvedState({
                        props: {},
                        resolvedSlots: {},
                    });
                }
            }
        };
        runResolutionRef.current = (options) => {
            const version = ++resolutionVersionRef.current;
            void run(version, options?.skipInlineValidation);
        };
        const version = ++resolutionVersionRef.current;
        void run(version, false);
        return () => {
            cancelled = true;
        };
    }, [node, modifiers, formStore, currentPath, effectivePathModifiers, stylePlatform, styleBreakpoint, componentStore, componentPath]);
    /* eslint-enable react-hooks/exhaustive-deps */
    return {
        resolvedState,
        resolveError,
        dependenciesRef,
        runResolutionRef,
    };
};

const buildRenderNodeEventProps = ({ node, modifiers, actions, formStore, currentPath, componentProps, effectivePathModifiers, validators, translations, defaultLanguage, activeLanguage, }) => {
    const actionCtx = {
        formStore,
        currentPath,
        pathModifiers: effectivePathModifiers,
        validators,
        translations,
        defaultLanguage,
        activeLanguage,
        componentProps,
    };
    const eventProps = {};
    for (const [key, value] of Object.entries(node)) {
        // don't delete it, it's important if we change the logic in future.
        // if (key.startsWith('$')) continue
        // TODO check if there is a limitation somewhere else
        // TODO need to add documentation
        if (!key.startsWith('on'))
            continue;
        let handler;
        //TODO: it could be array id need multiple action one by one
        if (Array.isArray(value)) {
            const handlers = value
                .map((v) => resolveAction(v, actions, modifiers, actionCtx))
                .filter((h) => !!h);
            if (handlers.length) {
                handler = async (e) => {
                    for (const h of handlers) {
                        await h(e);
                    }
                };
            }
        }
        else {
            handler = resolveAction(value, actions, modifiers, actionCtx);
        }
        if (handler)
            eventProps[key] = handler;
    }
    return eventProps;
};

const buildComponentContext = ({ formStore, modifiers, actions, currentPath, effectivePathModifiers, fieldErrors, fieldTouched, }) => {
    const ctx = {
        actions,
        currentPath,
        formStore,
        modifiers,
        pathModifiers: effectivePathModifiers,
    };
    if (fieldErrors !== undefined)
        ctx.fieldErrors = fieldErrors;
    if (fieldTouched !== undefined)
        ctx.fieldTouched = fieldTouched;
    return ctx;
};
const hasAnyError = (value) => {
    if (value === null || value === undefined)
        return false;
    if (Array.isArray(value))
        return value.some((v) => hasAnyError(v));
    if (typeof value === 'object')
        return Object.values(value).some((v) => hasAnyError(v));
    return true;
};
const resolveExplicitFieldErrors = ({ node, formStore }) => {
    const valueSpec = node.value;
    if (!valueSpec || typeof valueSpec !== 'object' || !(MODIFIER_KEY in valueSpec) || valueSpec[MODIFIER_KEY] !== 'get') {
        return undefined;
    }
    const storeName = valueSpec.store;
    const valuePath = valueSpec.path;
    if (!storeName || !valuePath)
        return undefined;
    const errorValue = formStore.getForStore(`${storeName}${ERROR_STORE_SUFFIX}`, valuePath);
    return hasAnyError(errorValue) ? errorValue : undefined;
};

const coercePrimitiveChild = (value) => {
    if (value === true)
        return 'true';
    if (value === false)
        return 'false';
    if (value === null)
        return 'null';
    if (value === undefined)
        return undefined;
    if (React.isValidElement(value))
        return value;
    if (typeof value === 'object') {
        if (MODIFIER_KEY in value) {
            return undefined;
        }
        try {
            return JSON.stringify(value);
        }
        catch {
            return '[invalid]';
        }
    }
    return value;
};

const computeRenderNodeSlotChildren = ({ node, resolvedSlots, effectivePathModifiers, pathModifiers, currentPath, formStore, components, modifiers, actions, validators, translations, defaultLanguage, activeLanguage, renderNested, }) => {
    const nestedPropsBase = {
        components,
        modifiers,
        actions,
        formStore,
        validators,
        translations,
        defaultLanguage,
        activeLanguage,
    };
    const nodeListConfig = LIST_SEMAPHORE in node && node[LIST_ITEM] ? node : null;
    const listConfigForSlot = (slotKey, slotRaw) => {
        if (slotKey === V_CHILDREN && LIST_SEMAPHORE in node && node[LIST_ITEM]) {
            return node;
        }
        if (typeof slotRaw === 'object' && slotRaw !== null && LIST_SEMAPHORE in slotRaw) {
            return slotRaw;
        }
        return null;
    };
    const renderListItemTemplate = (listItem, itemPath, itemPathModifiers) => {
        if (Array.isArray(listItem)) {
            return listItem.map((child, childIndex) => (jsx(React.Fragment, { children: renderListItemTemplate(child, itemPath, itemPathModifiers) }, childIndex)));
        }
        if (listItem && typeof listItem === 'object' && V_COMP in listItem) {
            return renderNested({
                node: listItem,
                ...nestedPropsBase,
                currentPath: itemPath,
                pathModifiers: itemPathModifiers,
            });
        }
        return coercePrimitiveChild(listItem);
    };
    const renderListFromConfig = (listConfig) => {
        const listItem = listConfig[LIST_ITEM];
        const listPathModifiersRaw = listConfig[PATH_MODIFIERS_KEY];
        const listPathModifiers = isRecord(listPathModifiersRaw) ? listPathModifiersRaw : undefined;
        const effectivePathModifiersSafe = isRecord(effectivePathModifiers) ? effectivePathModifiers : undefined;
        const effKeys = effectivePathModifiersSafe !== undefined ? Object.keys(effectivePathModifiersSafe) : [];
        const listPm = listPathModifiers ?? {};
        const storeName = effKeys.find((k) => Object.prototype.hasOwnProperty.call(listPm, k)) ?? Object.keys(listPm)[0];
        if (typeof storeName !== 'string' || storeName.length === 0)
            return null;
        const effectiveStorePath = effectivePathModifiersSafe?.[storeName]?.path;
        const listStoreModifier = listPathModifiers?.[storeName];
        const listStorePath = isRecord(listStoreModifier) && typeof listStoreModifier.path === 'string' ? listStoreModifier.path : undefined;
        const baseListPath = typeof effectiveStorePath === 'string' && effectiveStorePath.length > 0
            ? effectiveStorePath
            : typeof listStorePath === 'string' && listStorePath.length > 0
                ? resolveStorePath(listStorePath, currentPath, pathModifiers, storeName)
                : undefined;
        if (baseListPath == null || baseListPath === '')
            return null;
        const listData = formStore.getForStore(storeName, baseListPath);
        if (!Array.isArray(listData))
            return null;
        const { indices } = computeListSliceRange({
            realDataLength: listData.length,
            page: listConfig[LIST_PAGE],
            itemPerPage: listConfig[LIST_ITEM_PER_PAGE],
            listLength: listConfig[LIST_LENGTH],
        });
        return indices.map((i) => {
            const itemPath = baseListPath ? `${baseListPath}/${i}` : `/${i}`;
            const itemPathModifiers = storeName && baseListPath
                ? {
                    ...(effectivePathModifiersSafe ?? {}),
                    [storeName]: { path: `${baseListPath}/${i}` },
                }
                : effectivePathModifiersSafe;
            return (jsx(React.Fragment, { children: renderListItemTemplate(listItem, itemPath, itemPathModifiers) }, i));
        });
    };
    const renderPlainSlotContent = (raw, resolvedVal) => {
        if (Array.isArray(raw)) {
            const resolvedArr = resolvedVal;
            return raw.map((child, i) => jsx(React.Fragment, { children: renderPlainSlotContent(child, resolvedArr?.[i]) }, i));
        }
        if (raw && typeof raw === 'object' && V_COMP in raw) {
            return renderNested({
                node: raw,
                ...nestedPropsBase,
                currentPath,
                pathModifiers: effectivePathModifiers,
            });
        }
        const val = resolvedVal !== undefined ? resolvedVal : raw;
        if (Array.isArray(val)) {
            return val.map((child, i) => typeof child === 'object' && child !== null && V_COMP in child ? (jsx(React.Fragment, { children: renderNested({
                    node: child,
                    ...nestedPropsBase,
                    currentPath,
                    pathModifiers: effectivePathModifiers,
                }) }, i)) : (coercePrimitiveChild(child)));
        }
        if (typeof val === 'object' && val !== null && V_COMP in val) {
            return renderNested({
                node: val,
                ...nestedPropsBase,
                currentPath,
                pathModifiers: effectivePathModifiers,
            });
        }
        return coercePrimitiveChild(val);
    };
    let mainChildren = nodeListConfig ? renderListFromConfig(nodeListConfig) : null;
    const multiChildSlots = {};
    const slotsResolved = resolvedSlots ?? {};
    for (const [key, value] of Object.entries(node)) {
        if (!key.startsWith('$child'))
            continue;
        const resolved = slotsResolved[key];
        const listCfg = listConfigForSlot(key, value);
        let slotChildren = null;
        if (listCfg) {
            slotChildren = renderListFromConfig(listCfg);
        }
        else if (value !== undefined) {
            if (Array.isArray(value)) {
                const resolvedArr = resolved;
                slotChildren = value.map((child, i) => jsx(React.Fragment, { children: renderPlainSlotContent(child, resolvedArr?.[i]) }, i));
            }
            else {
                slotChildren = renderPlainSlotContent(value, resolved);
            }
        }
        if (key === V_CHILDREN) {
            if (!nodeListConfig) {
                mainChildren = slotChildren;
            }
        }
        else {
            multiChildSlots[key] = slotChildren;
        }
    }
    return { mainChildren, multiChildSlots };
};

const sanitizePathModifiers = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return undefined;
    const sanitized = {};
    for (const [storeName, spec] of Object.entries(value)) {
        if (!spec || typeof spec !== 'object' || Array.isArray(spec))
            continue;
        const rawPath = spec.path;
        if (typeof rawPath !== 'string' || rawPath.length === 0)
            continue;
        sanitized[storeName] = { path: rawPath };
    }
    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};
const RenderNodeInner = (props) => {
    const { node: origNode, components, modifiers, actions, formStore, currentPath, pathModifiers, validators, translations, defaultLanguage, activeLanguage, } = props;
    const node = useMemo(() => expandSimplifiedNode(origNode), [origNode]);
    const s = origNode.store;
    const componentStore = typeof s === 'string' && s.length > 0 ? s : undefined;
    const p = origNode.path;
    const componentPath = typeof p === 'string' ? p : undefined;
    const styleConfig = useStyleConfig();
    const ownPathModifiers = useMemo(() => sanitizePathModifiers(getOwnPathModifiers(node)), [node]);
    const effectivePathModifiers = useMemo(() => mergeEffectivePathModifiers({
        ownPathModifiers,
        pathModifiers,
        currentPath,
    }), [ownPathModifiers, pathModifiers, currentPath]);
    const { resolvedState, resolveError } = useRenderNodeResolution({
        node,
        modifiers,
        formStore,
        currentPath,
        effectivePathModifiers,
        validators,
        translations,
        defaultLanguage,
        activeLanguage,
        stylePlatform: styleConfig.platform,
        styleBreakpoint: styleConfig.breakpoint,
        componentStore,
        componentPath,
    });
    const renderNested = useCallback((p) => jsx(RenderNode, { ...p }), []);
    if (typeof origNode !== 'object') {
        return null;
    }
    if (resolveError) {
        console.error('[JsonUI] resolveModifier error:', resolveError);
        return jsxs("div", { style: { padding: 8, color: '#c00', fontSize: 12 }, children: ["Error: ", resolveError.message] });
    }
    if (resolvedState === null) {
        return null;
    }
    const { props: resolvedProps, resolvedSlots } = resolvedState;
    const compName = node[V_COMP];
    if (!compName)
        return null;
    const fallbackUndefined = components._Undefined ?? builtinComponents._Undefined;
    const Comp = components[compName] ?? fallbackUndefined;
    // Extract context-specific props from resolvedProps so they don't bleed into HTML attrs
    const { fieldErrors: resolvedFieldErrors, fieldTouched, ...$ownResolvedProps } = resolvedProps;
    // For the explicit binding case (value: { $modifier: 'get', ... }) resolve errors from store
    const explicitFieldErrors = resolveExplicitFieldErrors({ node, formStore });
    const $ctx = buildComponentContext({
        formStore,
        modifiers,
        actions,
        currentPath,
        effectivePathModifiers,
        fieldErrors: resolvedFieldErrors ?? explicitFieldErrors,
        fieldTouched,
    });
    const { mainChildren, multiChildSlots } = computeRenderNodeSlotChildren({
        node,
        resolvedSlots,
        effectivePathModifiers,
        pathModifiers,
        currentPath,
        formStore,
        components,
        modifiers,
        actions,
        validators,
        translations,
        defaultLanguage,
        activeLanguage,
        renderNested,
    });
    // Keep flat resolvedProps (with fieldErrors/fieldTouched) for action context so
    // action handlers that read componentProps still receive these values.
    const componentActionProps = {
        ...resolvedProps,
    };
    const eventProps = buildRenderNodeEventProps({
        node,
        modifiers,
        actions,
        formStore,
        currentPath,
        componentProps: componentActionProps,
        effectivePathModifiers,
        validators,
        translations,
        defaultLanguage,
        activeLanguage,
    });
    const mergedProps = {
        ...$ownResolvedProps,
        $ctx,
        ...multiChildSlots,
        ...eventProps,
    };
    if (mergedProps.style != null && (typeof mergedProps.style !== 'object' || Array.isArray(mergedProps.style))) {
        delete mergedProps.style;
    }
    if (components[compName] === undefined) {
        mergedProps.compName = compName;
    }
    return jsx(Comp, { ...mergedProps, children: mainChildren });
};
const propsAreEqual = (prev, next) => {
    return (prev.node === next.node &&
        prev.components === next.components &&
        prev.modifiers === next.modifiers &&
        prev.actions === next.actions &&
        prev.formStore === next.formStore &&
        prev.currentPath === next.currentPath &&
        prev.pathModifiers === next.pathModifiers &&
        prev.validators === next.validators &&
        prev.translations === next.translations &&
        prev.defaultLanguage === next.defaultLanguage &&
        prev.activeLanguage === next.activeLanguage);
};
const RenderNode = React.memo(RenderNodeInner, propsAreEqual);

/** Main JsonUI parity: host registers a callback; JsonUI wires store updates into it. */
class MessageHandler {
    constructor() {
        this.set = (newFunc) => {
            this.updateDefaultValueHandler = newFunc;
        };
        this.get = () => {
            return this.updateDefaultValueHandler;
        };
    }
}
const MessageHandlerContext = createContext(null);

const normalizeLogicalPath = (path) => {
    const p = path.trim();
    if (!p || p === '/')
        return '/';
    const withSlash = p.startsWith('/') ? p : `/${p}`;
    return normalizePath(withSlash);
};
/**
 * Subscribes the root store setter to `MessageHandler` (main JsonUI parity).
 */
const MessageReceiver = ({ formStore }) => {
    const messageHandler = useContext(MessageHandlerContext);
    useEffect(() => {
        const changeDefaultValue = ({ store: storeName, path, value }) => {
            const logicalPath = normalizeLogicalPath(path);
            // Match main DATA_UPDATE: do not mark field touched for host-driven updates.
            formStore.setForStore(storeName, logicalPath, value, false);
        };
        messageHandler?.set(changeDefaultValue);
    }, [messageHandler, formStore]);
    return null;
};

const JsonUI = ({ model, components = {}, modifiers = {}, actions = {}, initialFormStore, defaultValues = {}, defaultLanguage = 'en', activeLanguage, platform = 'web', id, onStateExport, }) => {
    const modelRecord = isRecord(model) ? model : undefined;
    const formStore = useFormStore(initialFormStore, defaultValues);
    const validationRegistry = useMemo(() => {
        const rawValidations = modelRecord?.[V_VALIDATIONS];
        const validations = Array.isArray(rawValidations)
            ? rawValidations.filter((rule) => isRecord(rule) &&
                typeof rule.store === 'string' &&
                rule.store.length > 0 &&
                typeof rule.path === 'string' &&
                rule.path.length > 0 &&
                rule.schema !== undefined &&
                rule.schema !== null)
            : [];
        return buildValidationRegistry(validations);
    }, [modelRecord]);
    const allComponents = useMemo(() => {
        if (!isRecord(components))
            return builtinComponents;
        return Object.keys(components).length > 0 ? { ...builtinComponents, ...components } : builtinComponents;
    }, [components]);
    const resolvedLanguage = activeLanguage ?? defaultLanguage;
    // could we move the translation into a modifier?
    const translations = useMemo(() => {
        const raw = modelRecord?.$translate;
        if (!isRecord(raw))
            return {};
        const map = {};
        // New shape: { langCode: { key: value, ... }, ... }
        for (const [lang, table] of Object.entries(raw)) {
            if (!isRecord(table))
                continue;
            for (const [key, val] of Object.entries(table)) {
                if (typeof val !== 'string' || !key)
                    continue;
                const entry = (map[key] ?? (map[key] = {}));
                entry[lang] = val;
            }
        }
        return map;
    }, [modelRecord]);
    const idRef = useRef(id);
    useEffect(() => {
        idRef.current = id;
    }, [id]);
    useEffect(() => {
        return () => {
            if (onStateExport) {
                onStateExport({
                    id: idRef.current,
                    formState: formStore.getLogicalStoresMap(),
                });
            }
        };
    }, [model, defaultValues, onStateExport, id, formStore]);
    // TODO: the model could be something else, like array, number, string, boolean, etc.
    const modelUnknown = model;
    if (modelUnknown === null || typeof modelUnknown !== 'object') {
        return jsx("div", { style: { padding: 16, color: '#666' }, children: "JsonUI: no valid model" });
    }
    return (jsxs(StyleProvider, { platform: platform, children: [jsx(MessageReceiver, { formStore: formStore }), jsx(RenderNode, { node: model, components: allComponents, modifiers: modifiers, actions: actions, formStore: formStore, validators: validationRegistry, translations: translations, defaultLanguage: defaultLanguage, activeLanguage: resolvedLanguage, currentPath: "/", pathModifiers: undefined })] }));
};

export { Button, Edit, FormLayout, Fragment, Image, JsonUI, MessageHandler, MessageHandlerContext, MessageReceiver, RenderNode, Slider, StoreDebugger, SubmitButton, Text, Undefined, View, builtinComponents, JsonUI as default, parseNumber, toDisplayString, uncontrolledInputProps };
//# sourceMappingURL=index.js.map
